import { type ReactNode } from "react";
import { useBufferedCount, useGapDisclosed } from "../../state/stream-view.tsx";
import { defaultNumberFormat } from "../../components/index.ts";

/**
 * The live-tail status line (task 11.12; design 8.2 live-tail pause, 12.3 "unverifiable is never
 * green"). It PRESENTS the pause/buffer/overflow facts the 11.3 stream client already produces — it
 * does not implement any of them. The three facts come straight from the view store the client
 * writes:
 *
 * - `live`              — mirrored from the shared time-range toggle (the driver pushes it into the
 *                         client, so pausing the toggle is what makes the client buffer).
 * - `useBufferedCount()`— the "N buffered" count the client publishes while paused: how many deltas
 *                         are parked in its bounded pause buffer. The ordered replay on resume is
 *                         AUTOMATIC inside the client (it drains the buffer in arrival order and
 *                         folds the deltas into the view); this component only shows the count so the
 *                         operator knows how far behind the frozen tail has fallen.
 * - `useGapDisclosed()` — true after the pause buffer OVERFLOWED (or a resume_* snapshot bridged a
 *                         gap): the client discarded the incomplete range and is recovering with a
 *                         FRESH SNAPSHOT. We surface this prominently with the fresh-snapshot
 *                         explanation, never pretending the tail is complete.
 *
 * None of the buffering, replay or overflow logic lives here; see `lib/stream/pause-buffer.ts`,
 * `lib/stream/client.ts` and `state/stream-view.ts`.
 */
export function TailStatus(props: { live: boolean }): ReactNode {
  const buffered = useBufferedCount();
  const gapDisclosed = useGapDisclosed();

  return (
    <div className="evt-tail" data-live={props.live}>
      <div className="evt-tail__state" role="status" aria-live="polite">
        {props.live ? (
          <span className="evt-tail__badge" data-state="live">
            <i aria-hidden="true">●</i> Live tail
          </span>
        ) : (
          <span className="evt-tail__badge" data-state="paused">
            <i aria-hidden="true">❚❚</i> Paused
          </span>
        )}

        {/* While paused, the client is parking incoming deltas; show how many are waiting. The count
            is the client's own buffered-delta count — resuming replays them in order automatically. */}
        {!props.live && (
          <span className="evt-tail__buffered">
            {buffered > 0
              ? `${defaultNumberFormat(buffered)} buffered. Resume to replay in order`
              : "No new events buffered yet"}
          </span>
        )}
      </div>

      {/* The gap disclosure: the pause buffer overflowed (or a resume bridged a gap), so the client
          is recovering with a fresh snapshot. This is never green. The tail is known-incomplete
          until the fresh snapshot settles it. */}
      {gapDisclosed && (
        <p className="evt-tail__gap" role="alert">
          <i aria-hidden="true">▲</i> A gap occurred. Too many events arrived while paused to replay
          them in order. Showing a fresh snapshot; some events between the pause and now are not in
          this view.
        </p>
      )}
    </div>
  );
}
